import type { PassageAuthoring, PassageLexical, PassageSentence } from '../api/types'
import type {
  LexicalLearningActivityProgress,
  LexicalLearningItem,
  LexicalLearningPracticeStage,
  LexicalLearningStage,
  LexicalLearningStageProgress,
} from './types'
import { PRACTICE_STAGES } from './types.ts'

const WORD_CHARACTER = /[A-Za-z0-9]/u
const LEXICAL_TOKEN = /[A-Za-z0-9]+(?:['’-][A-Za-z0-9]+)*/gu
const APOSTROPHES = /[‘’‛ʼ＇]/gu

export const QUIZ_MINIMUM_SIZE = 4
export const QUIZ_MASTERY_STREAK = 1
export const LEARN_PRONUNCIATION_PASS_SCORE = 50
export const REVIEW_READING_ACCURACY_PASS_SCORE = 50

export function lexicalMeaning(lexical: PassageLexical): string {
  return lexical.translations.vi?.trim()
    || Object.values(lexical.translations).find((translation) => translation.trim())?.trim()
    || 'Chưa có nghĩa'
}

export function passageSentences(passage: PassageAuthoring): PassageSentence[] {
  return [passage.title, ...passage.paragraphs.flatMap((paragraph) => paragraph.sentences)]
}

export function passageSentenceEntries(passage: PassageAuthoring): Array<{ key: string; sentence: PassageSentence }> {
  return [
    { key: `title:${passage.title.id ?? 'draft'}`, sentence: passage.title },
    ...passage.paragraphs.flatMap((paragraph, paragraphIndex) => paragraph.sentences.map((sentence, sentenceIndex) => ({
      key: `paragraph:${paragraph.id ?? paragraphIndex}:sentence:${sentence.id ?? sentenceIndex}`,
      sentence,
    }))),
  ]
}

export function skimTokenKey(sentenceKey: string, tokenIndex: number): string {
  return `${sentenceKey}:token:${tokenIndex}`
}

export function buildPassageTokenLexicalMap(passage: PassageAuthoring): Record<string, string[]> {
  return Object.fromEntries(passageSentenceEntries(passage).flatMap(({ key, sentence }) => (
    (sentence.tokens ?? []).flatMap((_, tokenIndex) => {
      const lexicalIds = lexicalIdsForToken(sentence, tokenIndex)
      return lexicalIds.length > 0 ? [[skimTokenKey(key, tokenIndex), lexicalIds]] : []
    })
  )))
}

export function lexicalIdsForSelectedTokens(selectedTokenKeys: string[], tokenLexicalIdsByKey: Record<string, string[]>): string[] {
  return uniqueIds(selectedTokenKeys.flatMap((key) => tokenLexicalIdsByKey[key] ?? []))
}

export function representativeTokenKeys(selectedLexicalIds: string[], tokenLexicalIdsByKey: Record<string, string[]>): string[] {
  const selected = new Set(selectedLexicalIds)
  const uncovered = new Set(selectedLexicalIds)
  const result: string[] = []
  for (const [key, lexicalIds] of Object.entries(tokenLexicalIdsByKey)) {
    if (!lexicalIds.some((id) => uncovered.has(id)) || !lexicalIds.every((id) => selected.has(id))) continue
    result.push(key)
    lexicalIds.forEach((id) => uncovered.delete(id))
  }
  for (const lexicalId of uncovered) {
    const entry = Object.entries(tokenLexicalIdsByKey).find(([, lexicalIds]) => lexicalIds.includes(lexicalId))
    if (entry && !result.includes(entry[0])) result.push(entry[0])
  }
  return result
}

export function collectPassageLexicals(passage: PassageAuthoring): LexicalLearningItem[] {
  const byId = new Map<string, LexicalLearningItem>()
  for (const sentence of passageSentences(passage)) {
    if (!sentence.id) continue
    for (const lexical of sentence.lexicals) {
      if (!lexical.id) continue
      const context = {
        sentence_id: sentence.id,
        sentence_text: sentence.text,
        token_indexes: lexical.token_indexes ?? [],
      }
      const existing = byId.get(lexical.id)
      if (existing) {
        existing.contexts.push(context)
        continue
      }
      byId.set(lexical.id, {
        id: lexical.id,
        text: lexical.text,
        type: lexical.type,
        meaning: lexicalMeaning(lexical),
        phonemes: lexical.phonemes,
        audio: lexical.audio,
        image: lexical.image,
        contexts: [context],
      })
    }
  }
  return [...byId.values()]
}

export function lexicalIdsForToken(sentence: PassageSentence, tokenIndex: number): string[] {
  return [...new Set(sentence.lexicals.flatMap((lexical) => (
    lexical.id && lexical.token_indexes?.includes(tokenIndex) ? [lexical.id] : []
  )))]
}

export function buildQuizLexicalIds(selectedIds: string[], passageLexicalIds: string[]): string[] {
  const selected = uniqueKnownIds(selectedIds, passageLexicalIds)
  const passageIds = uniqueIds(passageLexicalIds)
  if (selected.length === 0 || passageIds.length < QUIZ_MINIMUM_SIZE) return []
  if (selected.length >= QUIZ_MINIMUM_SIZE) return selected
  const selectedSet = new Set(selected)
  return [...selected, ...passageIds.filter((id) => !selectedSet.has(id))]
    .slice(0, QUIZ_MINIMUM_SIZE)
}

export function normalizeRecallText(value: string): string {
  return value
    .normalize('NFKC')
    .replace(APOSTROPHES, "'")
    .toLocaleLowerCase()
    .replace(/[^a-z0-9'\-\s]/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
}

export function recallMatches(answer: string, lexical: string): boolean {
  return normalizeRecallText(answer) === normalizeRecallText(lexical)
}

export function maskedLexical(text: string, revealCount: number): string {
  const visibleCharacters = Math.max(0, revealCount)
  return text.replace(LEXICAL_TOKEN, (token) => {
    let revealed = 0
    return [...token].map((character) => {
      if (!WORD_CHARACTER.test(character)) return character
      if (revealed < visibleCharacters) {
        revealed += 1
        return character
      }
      return '_'
    }).join('')
  })
}

export function revealableCharacterCount(text: string): number {
  return Math.max(0, ...[...text.matchAll(LEXICAL_TOKEN)].map(([token]) => [...token].filter((character) => WORD_CHARACTER.test(character)).length))
}

export function stableShuffle<T>(values: T[], seed: string): T[] {
  let hash = 2166136261
  for (const character of seed) {
    hash ^= character.charCodeAt(0)
    hash = Math.imul(hash, 16777619)
  }
  const result = [...values]
  for (let index = result.length - 1; index > 0; index -= 1) {
    hash = Math.imul(hash ^ (hash >>> 13), 1274126177)
    const target = Math.abs(hash) % (index + 1)
    ;[result[index], result[target]] = [result[target], result[index]]
  }
  return result
}

export function choiceLexicalIds(targetId: string, poolIds: string[], seed: string): string[] {
  const distractors = stableShuffle(poolIds.filter((id) => id !== targetId), `${seed}:distractors`).slice(0, 3)
  return stableShuffle([targetId, ...distractors], `${seed}:options`)
}

export function reviewSpeakingPassed(value: {
  recognized_text: string
  target_text: string
  scores: { reading_accuracy: number }
  summary: { incorrect: number }
  words: Array<{ verdict: string }>
}, lexicalText: string): boolean {
  if (recallMatches(value.recognized_text, lexicalText)) return true
  return value.scores.reading_accuracy > REVIEW_READING_ACCURACY_PASS_SCORE
    && value.summary.incorrect === 0
    && value.words.every((word) => word.verdict !== 'incorrect')
}

export function emptyStageProgress(targetIds: string[] = []): LexicalLearningStageProgress {
  return {
    status: 'not_started',
    queue: uniqueIds(targetIds),
    current_index: 0,
    completed_ids: [],
    best_scores: {},
    streaks: {},
    reveal_counts: {},
    review_rounds: 0,
  }
}

export function emptyLexicalLearningProgress(): LexicalLearningActivityProgress {
  return {
    version: 1,
    current_stage: 'skim',
    selected_token_keys: [],
    selected_lexical_ids: [],
    quiz_lexical_ids: [],
    quiz_available: false,
    stages: Object.fromEntries(PRACTICE_STAGES.map((stage) => [stage, emptyStageProgress()])) as Record<LexicalLearningPracticeStage, LexicalLearningStageProgress>,
    completed: false,
  }
}

export function nextLexicalLearningStage(current: LexicalLearningStage, quizAvailable: boolean): LexicalLearningStage {
  switch (current) {
    case 'skim': return 'learn_pronunciation'
    case 'learn_pronunciation': return quizAvailable ? 'quiz_matching' : 'review_speaking'
    case 'quiz_matching': return 'quiz_meaning_to_lexical'
    case 'quiz_meaning_to_lexical': return 'quiz_lexical_to_meaning'
    case 'quiz_lexical_to_meaning': return 'review_speaking'
    case 'review_speaking': return 'review_typing'
    case 'review_typing': return 'complete'
    case 'complete': return 'complete'
  }
}

export function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids.filter(Boolean))]
}

export function uniqueKnownIds(ids: string[], knownIds: string[]): string[] {
  const known = new Set(knownIds)
  return uniqueIds(ids).filter((id) => known.has(id))
}
